
import { LocalStorageCache } from 'caching';
import { inject } from "fw";
import { Store, handle, dispatch } from "fw-state";

import { once } from "helpers/once";
import {
  LogoutAction,
  StartAction,
  SelectedContactTypeChangedAction,
  ContactOrganizationModelChangedAction
} from "state/actions";

import { Contact, IDuplicateContactResult } from 'models/contact';
import { ContactOrganization, ContactTypeDefinition } from "models/contact-organization";
import { GridColumn } from 'models/grid-column';

import { ContactRepository } from "network/contact-repository";
import { IEntitySelection } from "network/ats";
import { ContactsService } from "service/contacts";

import { capitalize, throttle } from "lodash-es";
import { WebSocketMessageAction } from 'state/web-socket-message';
import { filterEntityChangedMessage } from 'state/filter-websocket-message';
import { FeatureFlagService } from "service/feature-flag";

interface PotentialDuplicateContactStoreShape {
  organizationId: string;
  organization: ContactOrganization;
  userId: string;
  selectedContactType: ContactTypeDefinition;
  potentialDuplicates: IDuplicateContactResult[];
  selectedPotentialDuplicate: IDuplicateContactResult;
  contactsForSelectedPotentialDuplicate: Contact[];
  columns: GridColumn[];
  sort: string;
}

export class EnsurePotentialDuplicateContactStoreAction {
  constructor() { }
}

export class RefreshPotentialDuplicateContactStoreAction {
  constructor() { }
}

export class PotentialDuplicateSelectedAction {
  constructor(
    public potentialDuplicate: IDuplicateContactResult
  ) { }
}

export class MergeDuplicateContactsAction {
  constructor(
    public contactIds: string[]
  ) { }
}

export class RefreshPotentialDuplicatesAction { }

export class PotentialDuplicatesGridColumnsChangedAction {
  constructor(
    public columns: GridColumn[]
  ) { }
}

export class TogglePotentialDuplicateContactsSortAction {
  constructor(
    public sort: string
  ) { }
}

@inject
export class PotentialDuplicateContactStore extends Store<PotentialDuplicateContactStoreShape> {
  public static DEFAULT_SORT: string = 'last first company';

  private throttledUpdate: Function = () => { };

  constructor(
    private contactRepo: ContactRepository,
    private cache: LocalStorageCache,
    private contactsService: ContactsService,
    private ffs: FeatureFlagService,
  ) {
    super();
    this.throttledUpdate = throttle(() => dispatch(new RefreshPotentialDuplicatesAction()), 60000);
  }

  get defaultColumns() {
    return this.contactsService
      ? this.contactsService.defaultGridColumns
      : [];
  }

  protected defaultState() {
    return {
      organizationId: null,
      organization: null,
      userId: null,
      selectedContactType: null,
      potentialDuplicates: [],
      selectedPotentialDuplicate: null,
      contactsForSelectedPotentialDuplicate: [],
      columns: this.defaultColumns,
      sort: PotentialDuplicateContactStore.DEFAULT_SORT
    };
  }

  @handle(StartAction)
  private async handleStartAction(action: StartAction) {
    const initialState = this.defaultState();

    const organization = action.context.ContactOrganization;
    initialState.organizationId = organization.id;
    initialState.organization = organization;
    const userId = action.context.Me.Id;
    initialState.userId = userId;


    const contactTypeKey = action.context.UserSeasonSettings.Settings["contactsTypeSelected"];
    const contactType: ContactTypeDefinition = organization.contact_types.find(t => t.key === contactTypeKey);

    if (contactType != null) {
      const cacheKey = this.createSortCacheKey(organization.id, userId, contactType.key);
      initialState.sort = this.cache.get<string>(cacheKey) || initialState.sort;
      initialState.columns = action.context.UserSeasonSettings.Settings[`potentialDuplicatesGridColumns${capitalize(contactType.key)}`] || this.defaultColumns;
      initialState.selectedContactType = contactType;
    }

    this.setState(s => initialState);
  }

  @handle(ContactOrganizationModelChangedAction)
  async handleContactOrganizationModelChangedAction(action: ContactOrganizationModelChangedAction) {
    this.setState(state => ({
      ...state,
      organization: action.organization,
    }));
  }

  @handle(EnsurePotentialDuplicateContactStoreAction)
  private async handleEnsurePotentialDuplicateContactStoreAction(action: EnsurePotentialDuplicateContactStoreAction) {
    await once("ensure-potential-duplicates", async () => await this.potentialDuplicateContact())
  }

  @handle(RefreshPotentialDuplicateContactStoreAction)
  private async handleRefreshPotentialDuplicateContactStoreAction(action: RefreshPotentialDuplicateContactStoreAction) {
    await this.potentialDuplicateContact();
  }

  private async potentialDuplicateContact() {
    const { selectedContactType } = this.state;

    if (selectedContactType) {
      const potentialDuplicates = await this.contactRepo.listDuplicates(selectedContactType.key);
      const selectedPotentialDuplicate = potentialDuplicates.length === 0 ? null : potentialDuplicates[0];
      const contactsForSelectedPotentialDuplicate = potentialDuplicates.length === 0
        ? []
        : await this.getPotentialDuplicateContacts(
          selectedPotentialDuplicate,
          selectedContactType.key,
          this.state.sort
        );

      this.setState(state => ({
        ...state,
        potentialDuplicates,
        selectedPotentialDuplicate,
        contactsForSelectedPotentialDuplicate
      }));
    }
  }

  @handle(LogoutAction)
  private handleLogoutAction() {
    this.setState(s => this.defaultState());
  }

  @handle(SelectedContactTypeChangedAction)
  async handleSelectedContactTypeChangedAction(action: SelectedContactTypeChangedAction) {
    const key: string = this.state.selectedContactType?.key;
    if (action.type == key)
      return;

    if (action.type == null) {
      this.setState(state => ({
        ...state,
        selectedContactType: null,
        potentialDuplicates: [],
        columns: action.dedupeColumns,
        sort: null,
      }));
      return;
    }

    // const organization = await this.contactOrganizationRepository.getById(this.state.organizationId);
    const organization = this.state.organization;
    const contactType = organization.contact_types.find(t => t.key == action.type);
    const selectedContactTypeKey = contactType && contactType.key;
    if (selectedContactTypeKey == null) {
      this.setState(state => ({
        ...state,
        selectedContactType: null,
        potentialDuplicates: [],
        selectedPotentialDuplicate: null,
        contactsForSelectedPotentialDuplicate: [],
        columns: action.dedupeColumns,
        sort: null,
      }));
      return;
    }

    const cacheKey = this.createSortCacheKey(this.state.organizationId, this.state.userId, selectedContactTypeKey);
    const sort = this.cache.get<string>(cacheKey) || PotentialDuplicateContactStore.DEFAULT_SORT;

    const potentialDuplicates = await this.contactRepo.listDuplicates(selectedContactTypeKey);
    if (this.state.potentialDuplicates.length > 0) {
      const selected = this.state.potentialDuplicates[0];
      const contacts = await this.getPotentialDuplicateContacts(selected, selectedContactTypeKey, sort);

      this.setState(state => ({
        ...state,
        selectedContactType: contactType,
        potentialDuplicates: potentialDuplicates,
        selectedPotentialDuplicate: selected,
        contactsForSelectedPotentialDuplicate: contacts,
        columns: action.dedupeColumns,
        sort: sort
      }));

    } else {
      this.setState(state => ({
        ...state,
        selectedContactType: contactType,
        selectedContactTypeKey: selectedContactTypeKey,
        selectedPotentialDuplicate: null,
        contactsForSelectedPotentialDuplicate: [],
        columns: action.dedupeColumns,
        sort: null
      }));

    }
  }

  @handle(PotentialDuplicatesGridColumnsChangedAction)
  private async handlePotentialDuplicatesGridColumnsChangedAction(action: PotentialDuplicatesGridColumnsChangedAction) {
    this.setState(state => ({
      ...state,
      columns: action.columns
    }));
  }

  @handle(TogglePotentialDuplicateContactsSortAction)
  private async handleTogglePotentialDuplicateContactsSortAction(action: TogglePotentialDuplicateContactsSortAction) {
    const sort = (action.sort && action.sort == this.state.sort)
      ? `-(${action.sort})`
      : action.sort;
    const cacheKey = this.createSortCacheKey(this.state.organizationId, this.state.userId, this.state.selectedContactType?.key);
    this.cache.set<string>(cacheKey, sort);

    this.setState(state => ({
      ...state,
      sort: sort,
    }));

    await this.loadPotentialDuplicates();
  }

  @handle(PotentialDuplicateSelectedAction)
  private async handlePotentialDuplicateSelectedAction(action: PotentialDuplicateSelectedAction) {
    const contacts = await this.getPotentialDuplicateContacts(
      action.potentialDuplicate,
      this.state.selectedContactType?.key,
      this.state.sort
    );
    this.setState(state => ({
      ...state,
      selectedPotentialDuplicate: action.potentialDuplicate,
      contactsForSelectedPotentialDuplicate: contacts,
    }));
  }

  @handle(MergeDuplicateContactsAction)
  private async handleMergeDuplicateContactsAction(action: MergeDuplicateContactsAction) {
    const args = <IEntitySelection>{ contact_type: this.state.selectedContactType?.key, ids: action.contactIds };
    await this.contactRepo.mergeSelection(args);
    await dispatch(new RefreshPotentialDuplicatesAction());
  }

  @handle(RefreshPotentialDuplicatesAction)
  private async handleRefreshPotentialDuplicatesAction(action: RefreshPotentialDuplicatesAction) {
    await this.loadPotentialDuplicates();
  }

  private createSortCacheKey(organizationId: string, userId: string, contactTypeKey: string): string {
    return `${organizationId}:${userId}-potential-duplicates-${contactTypeKey}-sort`;
  }

  private async loadPotentialDuplicates(): Promise<void> {
    const defaultState = this.defaultState();
    defaultState.organizationId = this.state.organizationId;
    defaultState.organization = this.state.organization;
    defaultState.userId = this.state.userId;

    if (this.state.selectedContactType?.key) {
      // these initial values should already be set
      defaultState.selectedContactType = this.state.selectedContactType;
      defaultState.columns = this.state.columns;
      defaultState.sort = this.state.sort;

      defaultState.potentialDuplicates = await this.contactRepo.listDuplicates(defaultState.selectedContactType.key);
      if (defaultState.potentialDuplicates.length > 0) {
        let selectedPotentialDuplicate: IDuplicateContactResult = null;
        // If we already had a selected potential duplicate, let's see if we can still
        // use that as the initial selected potential duplicate.
        if (this.state.selectedPotentialDuplicate) {
          selectedPotentialDuplicate = defaultState.potentialDuplicates.find(duplicate => duplicate.matching_key == this.state.selectedPotentialDuplicate.matching_key);
        }

        // We had no starting/selected duplicate, just pick the first one in the list.
        if (selectedPotentialDuplicate == null) {
          selectedPotentialDuplicate = defaultState.potentialDuplicates[0];
        }

        defaultState.selectedPotentialDuplicate = selectedPotentialDuplicate;
        defaultState.contactsForSelectedPotentialDuplicate = await this.getPotentialDuplicateContacts(
          defaultState.selectedPotentialDuplicate,
          defaultState.selectedContactType && defaultState.selectedContactType.key,
          defaultState.sort
        );
      }
    }

    this.setState(s => defaultState);
  }

  

  private async getPotentialDuplicateContacts(potentialDuplicate: IDuplicateContactResult, contactTypeKey: string, sort: string) {
    const queryString = `${potentialDuplicate.field}:"${potentialDuplicate.matching_key}"`;
    const f = !contactTypeKey ? null : `type:${contactTypeKey}`;
    const response = await this.contactRepo.list(queryString, f, null, sort, 1, potentialDuplicate.total, contactTypeKey);
    return response.results;
  }

  @handle(WebSocketMessageAction, filterEntityChangedMessage("PersistentContact"))
  private async handleEntityChangedAction(action: WebSocketMessageAction) {
    this.throttledUpdate();
  }
}
